import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'info',
      title: { en: 'New user registered', ar: 'مستخدم جديد مسجل' },
      message: { en: 'John Doe has joined the platform', ar: 'انضم جون دو إلى المنصة' },
      time: '5 min ago',
      unread: true
    },
    {
      id: 2,
      type: 'warning',
      title: { en: 'Workshop capacity alert', ar: 'تنبيه سعة الورشة' },
      message: { en: 'Workshop #42 is at 95% capacity', ar: 'الورشة رقم 42 بسعة 95٪' },
      time: '15 min ago',
      unread: true
    },
    {
      id: 3,
      type: 'success',
      title: { en: 'Report generated', ar: 'تم إنشاء التقرير' },
      message: { en: 'Monthly analytics report is ready', ar: 'تقرير التحليلات الشهري جاهز' },
      time: '1 hour ago',
      unread: false
    },
    {
      id: 4,
      type: 'error',
      title: { en: 'System alert', ar: 'تنبيه النظام' },
      message: { en: 'Database backup failed', ar: 'فشل النسخ الاحتياطي لقاعدة البيانات' },
      time: '2 hours ago',
      unread: false
    }
  ]);

  const panelRef = useRef(null);
  const language = localStorage.getItem('language') || 'ar';
  const unreadCount = notifications?.filter(n => n?.unread)?.length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef?.current && !panelRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (id) => {
    setNotifications(prev =>
      prev?.map(n => n?.id === id ? { ...n, unread: false } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev?.map(n => ({ ...n, unread: false })));
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: 'Info',
      warning: 'AlertTriangle',
      success: 'CheckCircle',
      error: 'XCircle'
    };
    return icons?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colors = {
      info: 'text-primary',
      warning: 'text-warning',
      success: 'text-success',
      error: 'text-error'
    };
    return colors?.[type] || 'text-text-secondary';
  };

  return (
    <div className="notification-center" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="notification-button"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header flex items-center justify-between">
            <h3 className="text-base font-semibold">
              {language === 'en' ? 'Notifications' : 'الإشعارات'}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-primary hover:underline"
              >
                {language === 'en' ? 'Mark all read' : 'تحديد الكل كمقروء'}
              </button>
            )}
          </div>

          <div className="notification-list scrollbar-custom">
            {notifications?.length === 0 ? (
              <div className="px-4 py-8 text-center text-text-secondary">
                <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {language === 'en' ? 'No notifications' : 'لا توجد إشعارات'}
                </p>
              </div>
            ) : (
              notifications?.map((notification) => (
                <div
                  key={notification?.id}
                  onClick={() => handleNotificationClick(notification?.id)}
                  className={`notification-item ${notification?.unread ? 'unread' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      name={getNotificationIcon(notification?.type)}
                      size={20}
                      className={getNotificationColor(notification?.type)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground mb-1">
                        {notification?.title?.[language]}
                      </p>
                      <p className="text-xs text-text-secondary mb-1">
                        {notification?.message?.[language]}
                      </p>
                      <p className="text-xs text-text-secondary caption">
                        {notification?.time}
                      </p>
                    </div>
                    {notification?.unread && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;